/**
 * Vincent #148 (Eric 2026-05-16) — Page Audit BYOK.
 *
 * Affiche pour l'org active : statut des 3 clés (OpenAI / AssemblyAI / OVH)
 * + checklist détaillée de TOUS les call-sites IA backend avec leur statut
 * BYOK. Permet à un cabinet (Vincent, Eric Roy) de vérifier que 100% de la
 * consommation IA est facturée à leur propre compte et non à la plateforme.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import {
  ShieldCheck, Warning, CheckCircle, SpinnerGap, ArrowLeft,
  Robot, Microphone, CloudArrowUp,
} from "@phosphor-icons/react";
import { API_BASE as API } from "../lib/apiBase";

const SERVICE_META = {
  openai:      { icon: Robot,        label: "OpenAI",      color: "violet"  },
  assemblyai:  { icon: Microphone,   label: "AssemblyAI",  color: "amber"   },
  ovh_storage: { icon: CloudArrowUp, label: "OVHcloud",    color: "emerald" },
};

export default function AIPackageAuditPage() {
  const { lang } = useLang();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/org/ai-package/audit`, { withCredentials: true });
        setAudit(data);
      } catch (e) {
        setError(e.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 text-muted-foreground py-12" data-testid="audit-loading">
        <SpinnerGap size={20} className="animate-spin" /> {lang === "fr" ? "Audit en cours…" : "Auditing…"}
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-rose-600" data-testid="audit-error">{error}</div>;
  }

  const isComplete = audit?.overall_status === "complete";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="byok-audit-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck size={26} weight="duotone" className="text-teal-600" />
            {lang === "fr" ? "Audit BYOK Forfait AI" : "BYOK AI Package Audit"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === "fr"
              ? "Vérification que 100% de la consommation IA est facturée à votre propre compte (Loi 25 + facturation directe)."
              : "Verifies that 100% of AI usage is billed to your own account (Law 25 + direct billing)."}
          </p>
        </div>
        <Link
          to="/dashboard/settings/ai-package"
          className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline"
          data-testid="audit-back-link"
        >
          <ArrowLeft size={12} /> {lang === "fr" ? "Retour Forfait AI" : "Back to AI Package"}
        </Link>
      </div>

      {/* Overall status banner */}
      <div
        className={`px-5 py-4 rounded-xl border-2 ${
          isComplete
            ? "bg-teal-50 border-teal-300 text-teal-900"
            : "bg-amber-50 border-amber-300 text-amber-900"
        }`}
        data-testid="audit-overall-banner"
      >
        <div className="flex items-center gap-3">
          {isComplete ? <CheckCircle size={32} weight="fill" /> : <Warning size={32} weight="fill" />}
          <div className="flex-1">
            <h2 className="text-lg font-bold">
              {isComplete
                ? (lang === "fr" ? "✓ Conformité BYOK complète" : "✓ Full BYOK compliance")
                : (lang === "fr" ? "⚠ Conformité BYOK incomplète" : "⚠ BYOK compliance incomplete")}
            </h2>
            <p className="text-sm mt-1">{audit.compliance_note}</p>
          </div>
        </div>
      </div>

      {/* Service status cards */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          {lang === "fr" ? "Statut des 3 clés" : "Status of the 3 keys"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(audit.services).map(([service, info]) => {
            const meta = SERVICE_META[service] || {};
            const Icon = meta.icon;
            const ok = info.configured;
            return (
              <div
                key={service}
                className={`border-2 rounded-xl p-4 ${
                  ok
                    ? "bg-teal-50 border-teal-200"
                    : "bg-rose-50 border-rose-200"
                }`}
                data-testid={`audit-service-${service}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {Icon && <Icon size={18} weight="duotone" className={ok ? "text-teal-700" : "text-rose-700"} />}
                  <span className="font-bold text-sm">{meta.label || service}</span>
                  <span className={`ml-auto text-xs font-semibold ${ok ? "text-teal-700" : "text-rose-700"}`}>
                    {ok ? "✓ OK" : "✗ MISSING"}
                  </span>
                </div>
                {ok && info.key_masked && (
                  <code className="text-[11px] text-muted-foreground font-mono">{info.key_masked}</code>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Checklist of call-sites */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          {lang === "fr" ? "Vérification des call-sites IA" : "AI call-sites checklist"}
        </h2>
        <p className="text-xs text-muted-foreground mb-3">
          {lang === "fr"
            ? "Liste exhaustive des endroits où Knoria appelle OpenAI/AssemblyAI/OVH. Chaque ligne indique le mécanisme BYOK utilisé."
            : "Exhaustive list of places where Knoria calls OpenAI/AssemblyAI/OVH. Each line shows the BYOK mechanism used."}
        </p>
        <div className="border border-border rounded-xl bg-card overflow-hidden">
          {audit.checklist.map((c, i) => {
            const ok = c.status === "ok";
            return (
              <div
                key={i}
                className={`px-4 py-3 flex items-start gap-3 ${i > 0 ? "border-t border-border" : ""}`}
                data-testid={`audit-checklist-${i}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                  ok ? "bg-teal-100 text-teal-700" : "bg-rose-100 text-rose-700"
                }`}>
                  {ok ? <CheckCircle size={15} weight="fill" /> : <Warning size={15} weight="fill" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{c.module}</div>
                  <code className="text-[10px] text-muted-foreground font-mono block mt-0.5 truncate">↳ {c.via}</code>
                  <p className="text-xs text-muted-foreground mt-1">{c.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-[10px] text-muted-foreground italic">
        {lang === "fr"
          ? "Audit calculé en temps réel à partir de la configuration BYOK de votre organisation et de la cartographie des call-sites IA backend Knoria."
          : "Audit computed in real-time from your org's BYOK configuration and Knoria's AI call-site map."}
      </div>
    </div>
  );
}
